import React, { useEffect, useState } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import './NewsManagement.css';

const WASTE_HARMONOGRAM_BANNER_KEY = 'wasteHarmonogramBanner';

const WASTE_TYPES = [
  { value: 'mixed', name: 'Zmieszane' },
  { value: 'plastic', name: 'Plastik' },
  { value: 'paper', name: 'Papier' },
  { value: 'glass', name: 'Szkło' },
  { value: 'bio', name: 'Bio' },
  { value: 'wielkogabaryt', name: 'Wielkogabarytowe' },
  { value: 'opony', name: 'Opony' },
];

export default function WasteScheduleManagement() {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [zones, setZones] = useState([]);
  const [showZonesModal, setShowZonesModal] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneFormData, setZoneFormData] = useState({ name: '', description: '' });
  const [zoneLocalities, setZoneLocalities] = useState({}); // { zoneId: [locality1, locality2, ...] }
  const [newLocalityInput, setNewLocalityInput] = useState({}); // { zoneId: 'input value' }
  const [formData, setFormData] = useState({
    zone_id: '',
    waste_type: 'mixed',
    waste_type_name: 'Zmieszane',
    collection_date: '',
    city: '',
    street: '',
    notes: '',
  });
  const [harmonogramBanner, setHarmonogramBanner] = useState('');
  const [savingHarmonogramBanner, setSavingHarmonogramBanner] = useState(false);

  useEffect(() => {
    loadSchedule();
    loadZones();
    loadHarmonogramBanner();
  }, []);

  useEffect(() => {
    // Load localities for all zones when zones are loaded
    if (zones.length > 0) {
      loadLocalitiesForZones();
    }
  }, [zones]);

  const loadZones = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('waste_zones')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setZones(data || []);
    } catch (error) {
      toast.error('Błąd podczas ładowania stref');
      console.error(error);
    }
  };

  const loadLocalitiesForZones = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('locality_regions')
        .select('zone_id, locality_name')
        .order('locality_name', { ascending: true });

      if (error) throw error;

      // Group localities by zone_id
      const grouped = {};
      (data || []).forEach(item => {
        if (!grouped[item.zone_id]) {
          grouped[item.zone_id] = [];
        }
        grouped[item.zone_id].push(item.locality_name);
      });

      setZoneLocalities(grouped);
    } catch (error) {
      console.error('Error loading localities:', error);
      // Don't show toast for this, it's not critical
    }
  };

  const handleAddLocality = async (zoneId, localityName) => {
    const trimmed = localityName.trim();
    if (!trimmed) {
      toast.error('Wprowadź nazwę miejscowości');
      return;
    }

    // Check for duplicates within the same zone
    const existingLocalities = zoneLocalities[zoneId] || [];
    if (existingLocalities.some(l => l.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('Ta miejscowość już istnieje w tej strefie');
      return;
    }

    try {
      const { error } = await supabaseAdmin
        .from('locality_regions')
        .insert({
          zone_id: zoneId,
          locality_name: trimmed,
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast.error('Ta miejscowość już istnieje w tej strefie');
        } else {
          throw error;
        }
        return;
      }

      toast.success('Miejscowość dodana');
      setNewLocalityInput({ ...newLocalityInput, [zoneId]: '' });
      await loadLocalitiesForZones();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas dodawania miejscowości');
      console.error('Error adding locality:', error);
    }
  };

  const handleRemoveLocality = async (zoneId, localityName) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć miejscowość "${localityName}"?`)) return;

    try {
      const { error } = await supabaseAdmin
        .from('locality_regions')
        .delete()
        .eq('zone_id', zoneId)
        .eq('locality_name', localityName);

      if (error) throw error;

      toast.success('Miejscowość usunięta');
      await loadLocalitiesForZones();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas usuwania miejscowości');
      console.error('Error removing locality:', error);
    }
  };

  const loadSchedule = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('waste_schedule')
        .select(`
          *,
          waste_zones (name)
        `)
        .order('collection_date', { ascending: true });

      if (error) throw error;
      setSchedule(data || []);
    } catch (error) {
      toast.error('Błąd podczas ładowania harmonogramu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadHarmonogramBanner = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', WASTE_HARMONOGRAM_BANNER_KEY)
        .maybeSingle();

      if (error) throw error;
      const v = data?.setting_value;
      if (v == null) {
        setHarmonogramBanner('');
        return;
      }
      setHarmonogramBanner(typeof v === 'string' ? v : '');
    } catch (error) {
      console.error('Error loading harmonogram banner:', error);
    }
  };

  const handleSaveHarmonogramBanner = async () => {
    setSavingHarmonogramBanner(true);
    try {
      const { error } = await supabaseAdmin
        .from('app_settings')
        .upsert(
          {
            setting_key: WASTE_HARMONOGRAM_BANNER_KEY,
            setting_value: harmonogramBanner.trim(),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'setting_key' }
        );

      if (error) throw error;
      toast.success('Komunikat zapisany');
    } catch (error) {
      toast.error(error.message || 'Nie udało się zapisać komunikatu');
      console.error(error);
    } finally {
      setSavingHarmonogramBanner(false);
    }
  };

  const handleWasteTypeChange = (value) => {
    const type = WASTE_TYPES.find(t => t.value === value);
    setFormData({
      ...formData,
      waste_type: value,
      waste_type_name: type.name,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Walidacja wymaganych pól
    if (!formData.zone_id || !formData.waste_type || !formData.waste_type_name || !formData.collection_date) {
      toast.error('Wypełnij wszystkie wymagane pola');
      return;
    }

    try {
      // Przygotuj dane do zapisu
      const scheduleData = {
        zone_id: formData.zone_id,
        waste_type: formData.waste_type.trim(),
        waste_type_name: formData.waste_type_name.trim(),
        collection_date: formData.collection_date,
        city: formData.city.trim() || null,
        street: formData.street.trim() || null,
        notes: formData.notes.trim() || null,
      };

      if (editingItem) {
        const { error } = await supabaseAdmin
          .from('waste_schedule')
          .update({
            ...scheduleData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingItem.id);

        if (error) {
          console.error('Supabase update error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw new Error(error.message || 'Błąd podczas aktualizacji wpisu');
        }
        toast.success('Wpis zaktualizowany');
      } else {
        const { error } = await supabaseAdmin
          .from('waste_schedule')
          .insert(scheduleData);

        if (error) {
          console.error('Supabase insert error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw new Error(error.message || 'Błąd podczas dodawania wpisu');
        }
        toast.success('Wpis dodany');
      }

      setShowModal(false);
      setEditingItem(null);
      setFormData({
        zone_id: '',
        waste_type: 'mixed',
        waste_type_name: 'Zmieszane',
        collection_date: '',
        city: '',
        street: '',
        notes: '',
      });
      loadSchedule();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas zapisywania');
      console.error('Error saving schedule:', error);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      zone_id: item.zone_id || '',
      waste_type: item.waste_type,
      waste_type_name: item.waste_type_name,
      collection_date: item.collection_date,
      city: item.city || '',
      street: item.street || '',
      notes: item.notes || '',
    });
    setShowModal(true);
  };

  const handleZoneSubmit = async (e) => {
    e.preventDefault();
    
    if (!zoneFormData.name.trim()) {
      toast.error('Wprowadź nazwę strefy');
      return;
    }

    try {
      if (editingZone) {
        const { error } = await supabaseAdmin
          .from('waste_zones')
          .update({
            name: zoneFormData.name.trim(),
            description: zoneFormData.description.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingZone.id);

        if (error) {
          console.error('Supabase update error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw new Error(error.message || 'Błąd podczas aktualizacji strefy');
        }
        toast.success('Strefa zaktualizowana');
      } else {
        const { error } = await supabaseAdmin
          .from('waste_zones')
          .insert({
            name: zoneFormData.name.trim(),
            description: zoneFormData.description.trim() || null,
          });

        if (error) {
          console.error('Supabase insert error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw new Error(error.message || 'Błąd podczas dodawania strefy');
        }
        toast.success('Strefa dodana');
      }

      // Reload zones FIRST to ensure the list is updated before closing modal
      await loadZones();
      
      // Reload localities after zones are updated
      await loadLocalitiesForZones();
      
      // Only close modal and reset form AFTER zones are loaded
      setShowZonesModal(false);
      setEditingZone(null);
      setZoneFormData({ name: '', description: '' });
    } catch (error) {
      toast.error(error.message || 'Błąd podczas zapisywania strefy');
      console.error('Error saving zone:', error);
    }
  };

  const handleZoneEdit = (zone) => {
    setEditingZone(zone);
    setZoneFormData({
      name: zone.name,
      description: zone.description || '',
    });
    setShowZonesModal(true);
  };

  const handleZoneDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tę strefę? Wszystkie przypisane harmonogramy zostaną również usunięte.')) return;

    try {
      const { error } = await supabaseAdmin
        .from('waste_zones')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Supabase delete error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw new Error(error.message || 'Błąd podczas usuwania strefy');
      }
      toast.success('Strefa usunięta');
      await loadZones(); // ✅ Upewnij się, że lista jest odświeżona
      await loadLocalitiesForZones(); // Reload localities after zones are updated
      await loadSchedule();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas usuwania strefy');
      console.error('Error deleting zone:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten wpis?')) return;

    try {
      const { error } = await supabaseAdmin
        .from('waste_schedule')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Supabase delete error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw new Error(error.message || 'Błąd podczas usuwania wpisu');
      }
      toast.success('Wpis usunięty');
      loadSchedule();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas usuwania');
      console.error('Error deleting schedule:', error);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="news-management">
      <div className="page-header">
        <h2>Zarządzanie odbiorem śmieci</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-secondary" onClick={() => setShowZonesModal(true)}>
            Zarządzaj strefami
          </button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            Dodaj wpis
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Komunikat w aplikacji (harmonogram)</h3>
        <p style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>
          Krótki komunikat wyświetlany w aplikacji mobilnej na ekranie harmonogramu — na czerwono, po informacji o dzisiejszym odbiorze,
          nad listą terminów (tylko gdy użytkownik ma przypisany adres i widzi harmonogram). Zostaw puste, aby wyłączyć.
        </p>
        <textarea
          className="form-textarea"
          rows={4}
          value={harmonogramBanner}
          onChange={(e) => setHarmonogramBanner(e.target.value)}
          placeholder="np. Zmiana terminu odbioru worków z plastikiem…"
          style={{ width: '100%', boxSizing: 'border-box' }}
        />
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={savingHarmonogramBanner}
            onClick={handleSaveHarmonogramBanner}
          >
            {savingHarmonogramBanner ? 'Zapisywanie…' : 'Zapisz komunikat'}
          </button>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Strefa</th>
              <th>Typ</th>
              <th>Data</th>
              <th>Lokalizacja</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {schedule.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">Brak wpisów</td>
              </tr>
            ) : (
              schedule.map((item) => (
                <tr key={item.id}>
                  <td>{item.waste_zones?.name || 'Brak strefy'}</td>
                  <td>{item.waste_type_name}</td>
                  <td>{new Date(item.collection_date).toLocaleDateString('pl-PL')}</td>
                  <td>{item.street ? `${item.street}, ` : ''}{item.city || ''}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleEdit(item)}
                      >
                        Edytuj
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(item.id)}
                      >
                        Usuń
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => {
          setShowModal(false);
          setEditingItem(null);
          setFormData({
            zone_id: '',
            waste_type: 'mixed',
            waste_type_name: 'Zmieszane',
            collection_date: '',
            city: '',
            street: '',
            notes: '',
          });
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingItem ? 'Edytuj wpis' : 'Dodaj wpis'}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowModal(false);
                  setEditingItem(null);
                  setFormData({
                    waste_type: 'mixed',
                    waste_type_name: 'Zmieszane',
                    collection_date: '',
                    city: '',
                    street: '',
                    notes: '',
                  });
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Strefa *</label>
                <select
                  className="form-input"
                  value={formData.zone_id}
                  onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
                  required
                >
                  <option value="">Wybierz strefę</option>
                  {zones.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {zone.name}
                    </option>
                  ))}
                </select>
                {zones.length === 0 && (
                  <p style={{ color: '#ff3b30', fontSize: '14px', marginTop: '4px' }}>
                    Brak stref. Dodaj strefę w "Zarządzaj strefami".
                  </p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Typ odpadów *</label>
                <select
                  className="form-input"
                  value={formData.waste_type}
                  onChange={(e) => handleWasteTypeChange(e.target.value)}
                  required
                >
                  {WASTE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Data wywozu *</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.collection_date}
                  onChange={(e) => setFormData({ ...formData, collection_date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Miejscowość</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Ulica</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Uwagi</label>
                <textarea
                  className="form-textarea"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Zaktualizuj' : 'Dodaj'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditingItem(null);
                    setFormData({
                      zone_id: '',
                      waste_type: 'mixed',
                      waste_type_name: 'Zmieszane',
                      collection_date: '',
                      city: '',
                      street: '',
                      notes: '',
                    });
                  }}
                >
                  Anuluj
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal zarządzania strefami */}
      {showZonesModal && (
        <div className="modal-overlay" onClick={() => {
          setShowZonesModal(false);
          setEditingZone(null);
          setZoneFormData({ name: '', description: '' });
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingZone ? 'Edytuj strefę' : 'Zarządzanie strefami'}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowZonesModal(false);
                  setEditingZone(null);
                  setZoneFormData({ name: '', description: '' });
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <form onSubmit={handleZoneSubmit}>
                <div className="form-group">
                  <label className="form-label">Nazwa strefy *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={zoneFormData.name}
                    onChange={(e) => setZoneFormData({ ...zoneFormData, name: e.target.value })}
                    placeholder="np. Ksawerów, Strefa A"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Opis (opcjonalnie)</label>
                  <textarea
                    className="form-textarea"
                    value={zoneFormData.description}
                    onChange={(e) => setZoneFormData({ ...zoneFormData, description: e.target.value })}
                    placeholder="Dodatkowy opis strefy"
                  />
                </div>
                {editingZone && (
                  <div className="form-group" style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '20px' }}>
                    <label className="form-label">Miejscowości w tej strefie</label>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        <input
                          type="text"
                          className="form-input"
                          value={newLocalityInput[editingZone.id] || ''}
                          onChange={(e) => setNewLocalityInput({ ...newLocalityInput, [editingZone.id]: e.target.value })}
                          placeholder="Wpisz nazwę miejscowości"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddLocality(editingZone.id, newLocalityInput[editingZone.id] || '');
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => handleAddLocality(editingZone.id, newLocalityInput[editingZone.id] || '')}
                        >
                          Dodaj
                        </button>
                      </div>
                      {(zoneLocalities[editingZone.id] || []).length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {zoneLocalities[editingZone.id].map((locality, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 12px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                              }}
                            >
                              <span>{locality}</span>
                              <button
                                type="button"
                                className="btn btn-danger"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                onClick={() => handleRemoveLocality(editingZone.id, locality)}
                              >
                                Usuń
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ color: '#999', fontSize: '14px', marginTop: '8px' }}>
                          Brak przypisanych miejscowości. Dodaj pierwszą miejscowość.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingZone ? 'Zaktualizuj' : 'Dodaj strefę'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditingZone(null);
                      setZoneFormData({ name: '', description: '' });
                    }}
                  >
                    Anuluj
                  </button>
                </div>
              </form>

              <div style={{ marginTop: '30px', borderTop: '1px solid #ddd', paddingTop: '20px' }}>
                <h4 style={{ marginBottom: '16px' }}>Lista stref</h4>
                {zones.length === 0 ? (
                  <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                    Brak stref. Dodaj pierwszą strefę.
                  </p>
                ) : (
                  <table className="table" style={{ marginTop: '0' }}>
                    <thead>
                      <tr>
                        <th>Nazwa</th>
                        <th>Opis</th>
                        <th>Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zones.map((zone) => (
                        <tr key={zone.id}>
                          <td>
                            <div>
                              <strong>{zone.name}</strong>
                              {(zoneLocalities[zone.id] || []).length > 0 && (
                                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                  <strong>Miejscowości:</strong> {zoneLocalities[zone.id].join(', ')}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{zone.description || '-'}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn btn-primary"
                                onClick={() => handleZoneEdit(zone)}
                              >
                                Edytuj
                              </button>
                              <button
                                className="btn btn-danger"
                                onClick={() => handleZoneDelete(zone.id)}
                              >
                                Usuń
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

